import React, { useEffect, useMemo, useState } from 'react';
import {
  AdminResearchDeskNoteDto,
  AdminUserDto,
  adminApi,
  isUnauthorized,
} from '../../services/adminApi';
import { getAdminTheme } from './adminTheme';

interface AdminResearchDeskScreenProps {
  isDarkMode: boolean;
  token: string;
  onUnauthorized: () => void;
}

type SortKey = keyof AdminResearchDeskNoteDto | 'username';

const AdminResearchDeskScreen: React.FC<AdminResearchDeskScreenProps> = ({ isDarkMode, token, onUnauthorized }) => {
  const theme = useMemo(() => getAdminTheme(isDarkMode), [isDarkMode]);

  const colors = useMemo(
    () => ({
      card: theme.cardBg,
      border: theme.border,
      text: theme.text,
      subText: theme.subText,
      hover: theme.hoverBg,
      inputBg: theme.inputBg,
      primary: theme.primary,
      danger: theme.danger,
      radius: theme.radius,
    }),
    [theme]
  );

  const [items, setItems] = useState<AdminResearchDeskNoteDto[] | null>(null);
  const [users, setUsers] = useState<AdminUserDto[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set());

  const [gridSearch, setGridSearch] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'createdAtUtc', dir: 'desc' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [criteriaDraft, setCriteriaDraft] = useState({
    createdFrom: '',
    createdTo: '',
    username: '',
    stockSymbol: '',
    associatedWith: '',
    title: '',
  });
  const [criteriaApplied, setCriteriaApplied] = useState(criteriaDraft);

  const inputStyle: React.CSSProperties = {
    width: '100%',
    height: 34,
    borderRadius: colors.radius,
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.inputBg,
    padding: '0 10px',
    color: colors.text,
    outline: 'none',
    boxSizing: 'border-box',
    fontSize: 13,
    fontWeight: 700,
  };

  const buttonStyle = (variant: 'primary' | 'neutral' | 'danger', disabled?: boolean): React.CSSProperties => {
    const base: React.CSSProperties = {
      height: 34,
      padding: '0 12px',
      borderRadius: colors.radius,
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.card,
      color: colors.text,
      cursor: disabled ? 'not-allowed' : 'pointer',
      fontSize: 13,
      fontWeight: 800,
      opacity: disabled ? 0.6 : 1,
      whiteSpace: 'nowrap',
    };

    if (variant === 'primary') return { ...base, border: 'none', backgroundColor: colors.primary, color: '#fff' };
    if (variant === 'danger') return { ...base, border: 'none', backgroundColor: colors.danger, color: '#fff' };
    return base;
  };

  const normalize = (v: unknown): string => (v ?? '').toString().trim().toLowerCase();

  const parseDate = (value: string): Date | null => {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const d = new Date(trimmed);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const refresh = async (opts?: { clearSelection?: boolean }) => {
    setLoading(true);
    setError(null);
    try {
      const [notes, usersList] = await Promise.all([
        adminApi.listAdminResearchDeskNotes(token),
        adminApi.listUsers(token),
      ]);
      setItems(notes);
      setUsers(usersList);
      if (opts?.clearSelection) setSelectedIds(new Set());
    } catch (e) {
      if (isUnauthorized(e)) {
        onUnauthorized();
        return;
      }
      setError(e instanceof Error ? e.message : 'Failed loading research desk');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh({ clearSelection: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const userLookup = useMemo(() => {
    const map = new Map<number, string>();
    (users ?? []).forEach((u) => map.set(u.id, u.username));
    return map;
  }, [users]);

  const applyCriteria = () => {
    setCriteriaApplied(criteriaDraft);
    setPage(1);
    setSelectedIds(new Set());
  };

  const clearCriteria = () => {
    const cleared = { createdFrom: '', createdTo: '', username: '', stockSymbol: '', associatedWith: '', title: '' };
    setCriteriaDraft(cleared);
    setCriteriaApplied(cleared);
    setGridSearch('');
    setPage(1);
    setSelectedIds(new Set());
  };

  const toggleSort = (key: SortKey) => {
    setSort((prev) => {
      if (prev.key !== key) return { key, dir: 'asc' };
      return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
    });
  };

  const sortIndicator = (key: SortKey): string => {
    if (sort.key !== key) return '';
    return sort.dir === 'asc' ? ' ▲' : ' ▼';
  };

  const filteredItems = useMemo(() => {
    const list = items ?? [];
    const q = normalize(gridSearch);

    const createdFrom = parseDate(criteriaApplied.createdFrom);
    const createdTo = parseDate(criteriaApplied.createdTo);

    const usernameFilter = criteriaApplied.username.trim();
    const stockSymbolFilter = criteriaApplied.stockSymbol.trim();
    const associatedWithFilter = criteriaApplied.associatedWith.trim();
    const titleFilter = criteriaApplied.title.trim();

    const result = list.filter((x) => {
      if (usernameFilter.length > 0) {
        const resolvedUsername = (userLookup.get(x.userId) ?? x.username ?? '').toString();
        if (!normalize(resolvedUsername).includes(normalize(usernameFilter))) return false;
      }
      if (associatedWithFilter.length > 0) {
        if (!normalize(x.associatedWith).includes(normalize(associatedWithFilter))) return false;
      }
      if (stockSymbolFilter.length > 0) {
        if (!normalize(x.stockSymbol).includes(normalize(stockSymbolFilter))) return false;
      }
      if (titleFilter.length > 0) {
        if (!normalize(x.title).includes(normalize(titleFilter))) return false;
      }

      if (createdFrom || createdTo) {
        const created = new Date(x.createdAtUtc);
        if (!Number.isNaN(created.getTime())) {
          if (createdFrom && created < createdFrom) return false;
          if (createdTo) {
            const createdToEnd = new Date(createdTo);
            createdToEnd.setHours(23, 59, 59, 999);
            if (created > createdToEnd) return false;
          }
        }
      }

      if (q.length > 0) {
        const haystack = [
          x.id,
          x.userId,
          userLookup.get(x.userId) ?? x.username,
          x.stockSymbol,
          x.title,
          x.associatedWith,
          x.createdAtUtc,
          x.html,
        ]
          .map((v) => (v ?? '').toString().toLowerCase())
          .join(' | ');
        if (!haystack.includes(q)) return false;
      }

      return true;
    });

    const compare = (a: AdminResearchDeskNoteDto, b: AdminResearchDeskNoteDto): number => {
      const dir = sort.dir === 'asc' ? 1 : -1;

      const getVal = (x: AdminResearchDeskNoteDto): string | number => {
        if (sort.key === 'username') return (userLookup.get(x.userId) ?? x.username ?? '').toString().toLowerCase();
        const v = (x as any)[sort.key];
        if (typeof v === 'number') return v;
        if (sort.key === 'createdAtUtc') {
          const d = new Date(v as string);
          return Number.isNaN(d.getTime()) ? 0 : d.getTime();
        }
        return (v ?? '').toString().toLowerCase();
      };

      const av = getVal(a);
      const bv = getVal(b);
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return av.toString().localeCompare(bv.toString()) * dir;
    };

    return result.slice().sort(compare);
  }, [criteriaApplied, gridSearch, items, sort.dir, sort.key, userLookup]);

  const usernameOptions = useMemo(() => {
    const list = (users ?? [])
      .map((u) => (u.username ?? '').toString().trim())
      .filter((u) => u.length > 0);
    return Array.from(new Set(list)).sort((a, b) => a.localeCompare(b));
  }, [users]);

  const total = filteredItems.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pageSafe = Math.min(Math.max(1, page), totalPages);

  useEffect(() => {
    if (page !== pageSafe) setPage(pageSafe);
  }, [page, pageSafe]);

  const pageItems = useMemo(() => {
    const start = (pageSafe - 1) * pageSize;
    return filteredItems.slice(start, start + pageSize);
  }, [filteredItems, pageSafe, pageSize]);

  const formatDateTime = (value?: string | null): string => {
    if (!value) return '';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
  };

  const toggleAllOnPage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const allSelected = pageItems.every((x) => next.has(x.id));
      pageItems.forEach((x) => {
        if (allSelected) next.delete(x.id);
        else next.add(x.id);
      });
      return next;
    });
  };

  const toggleOne = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const deleteSelected = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    setLoading(true);
    setError(null);
    try {
      await adminApi.deleteAdminResearchDeskNotes(token, { ids });
      await refresh({ clearSelection: true });
    } catch (e) {
      if (isUnauthorized(e)) {
        onUnauthorized();
        return;
      }
      setError(e instanceof Error ? e.message : 'Failed deleting notes');
    } finally {
      setLoading(false);
    }
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    marginBottom: 12,
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: colors.card,
    border: `1px solid ${colors.border}`,
    borderRadius: colors.radius,
    padding: 12,
  };

  const thStyle: React.CSSProperties = {
    textAlign: 'left',
    fontSize: 12,
    fontWeight: 900,
    color: colors.subText,
    padding: '10px 10px',
    borderBottom: `1px solid ${colors.border}`,
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    userSelect: 'none',
  };

  const tdStyle: React.CSSProperties = {
    fontSize: 13,
    fontWeight: 700,
    color: colors.text,
    padding: '10px 10px',
    borderBottom: `1px solid ${colors.border}`,
    verticalAlign: 'top',
  };

  const smallText: React.CSSProperties = { fontSize: 12, fontWeight: 700, color: colors.subText };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={headerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <input
            value={gridSearch}
            onChange={(e) => setGridSearch(e.target.value)}
            placeholder="Search in notes"
            style={{ ...inputStyle, width: 260 }}
          />
          <button style={buttonStyle('neutral', loading)} disabled={loading} onClick={() => refresh({ clearSelection: true })}>
            Refresh
          </button>
          <button style={buttonStyle('danger', loading || selectedIds.size === 0)} disabled={loading || selectedIds.size === 0} onClick={deleteSelected}>
            Delete Selected ({selectedIds.size})
          </button>
        </div>
        <div style={smallText}>
          {loading ? 'Loading…' : error ? `Error: ${error}` : `Total: ${total}`}
        </div>
      </div>

      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 10 }}>
          <div style={{ fontWeight: 900, fontSize: 13, color: colors.text }}>Search Criteria</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button style={buttonStyle('neutral', loading)} disabled={loading} onClick={applyCriteria}>
              Apply
            </button>
            <button style={buttonStyle('neutral', loading)} disabled={loading} onClick={clearCriteria}>
              Clear
            </button>
          </div>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(6, minmax(190px, 1fr))',
            gap: 10,
            overflowX: 'auto',
            paddingBottom: 4,
          }}
        >
          <div>
            <div style={{ ...smallText, marginBottom: 6 }}>Created From</div>
            <input value={criteriaDraft.createdFrom} onChange={(e) => setCriteriaDraft((p) => ({ ...p, createdFrom: e.target.value }))} placeholder="YYYY-MM-DD" style={inputStyle} />
          </div>
          <div>
            <div style={{ ...smallText, marginBottom: 6 }}>Created To</div>
            <input value={criteriaDraft.createdTo} onChange={(e) => setCriteriaDraft((p) => ({ ...p, createdTo: e.target.value }))} placeholder="YYYY-MM-DD" style={inputStyle} />
          </div>
          <div>
            <div style={{ ...smallText, marginBottom: 6 }}>Username</div>
            <input
              value={criteriaDraft.username}
              onChange={(e) => setCriteriaDraft((p) => ({ ...p, username: e.target.value }))}
              placeholder="Select or type..."
              list="admin-research-desk-username-options"
              style={inputStyle}
            />
            <datalist id="admin-research-desk-username-options">
              {usernameOptions.map((u) => (
                <option key={u} value={u} />
              ))}
            </datalist>
          </div>
          <div>
            <div style={{ ...smallText, marginBottom: 6 }}>Associated With</div>
            <input
              value={criteriaDraft.associatedWith}
              onChange={(e) => setCriteriaDraft((p) => ({ ...p, associatedWith: e.target.value }))}
              placeholder="e.g. Summary"
              style={inputStyle}
            />
          </div>
          <div>
            <div style={{ ...smallText, marginBottom: 6 }}>Stock Symbol</div>
            <input
              value={criteriaDraft.stockSymbol}
              onChange={(e) => setCriteriaDraft((p) => ({ ...p, stockSymbol: e.target.value }))}
              placeholder="e.g. AAPL"
              style={inputStyle}
            />
          </div>
          <div>
            <div style={{ ...smallText, marginBottom: 6 }}>Title</div>
            <input value={criteriaDraft.title} onChange={(e) => setCriteriaDraft((p) => ({ ...p, title: e.target.value }))} placeholder="contains…" style={inputStyle} />
          </div>
        </div>
      </div>

      <div style={cardStyle}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 980 }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, cursor: 'default' }}>
                  <input
                    type="checkbox"
                    checked={pageItems.length > 0 && pageItems.every((x) => selectedIds.has(x.id))}
                    onChange={toggleAllOnPage}
                    aria-label="Select all on page"
                  />
                </th>
                <th style={thStyle} onClick={() => toggleSort('id')}>
                  Id{sortIndicator('id')}
                </th>
                <th style={thStyle} onClick={() => toggleSort('userId')}>
                  User Id{sortIndicator('userId')}
                </th>
                <th style={thStyle} onClick={() => toggleSort('username')}>
                  Username{sortIndicator('username')}
                </th>
                <th style={thStyle} onClick={() => toggleSort('associatedWith')}>
                  Associated With{sortIndicator('associatedWith')}
                </th>
                <th style={thStyle} onClick={() => toggleSort('stockSymbol')}>
                  Stock{sortIndicator('stockSymbol')}
                </th>
                <th style={thStyle} onClick={() => toggleSort('title')}>
                  Title{sortIndicator('title')}
                </th>
                <th style={thStyle} onClick={() => toggleSort('createdAtUtc')}>
                  Created{sortIndicator('createdAtUtc')}
                </th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map((x) => {
                const isSelected = selectedIds.has(x.id);
                return (
                  <tr key={x.id} style={{ backgroundColor: isSelected ? colors.hover : 'transparent' }}>
                    <td style={tdStyle}>
                      <input type="checkbox" checked={isSelected} onChange={() => toggleOne(x.id)} aria-label={`Select note ${x.id}`} />
                    </td>
                    <td style={tdStyle}>{x.id}</td>
                    <td style={tdStyle}>{x.userId}</td>
                    <td style={tdStyle}>{userLookup.get(x.userId) ?? x.username ?? ''}</td>
                    <td style={tdStyle}>{x.associatedWith ?? ''}</td>
                    <td style={tdStyle}>{x.stockSymbol ?? ''}</td>
                    <td style={tdStyle}>{x.title ?? ''}</td>
                    <td style={tdStyle}>{formatDateTime(x.createdAtUtc)}</td>
                  </tr>
                );
              })}
              {!loading && pageItems.length === 0 ? (
                <tr>
                  <td style={{ ...tdStyle, color: colors.subText }} colSpan={8}>
                    No notes found.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button style={buttonStyle('neutral', loading || pageSafe <= 1)} disabled={loading || pageSafe <= 1} onClick={() => setPage(1)}>
              ⏮
            </button>
            <button style={buttonStyle('neutral', loading || pageSafe <= 1)} disabled={loading || pageSafe <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              Prev
            </button>
            <div style={smallText}>
              Page {pageSafe} / {totalPages}
            </div>
            <button
              style={buttonStyle('neutral', loading || pageSafe >= totalPages)}
              disabled={loading || pageSafe >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </button>
            <button style={buttonStyle('neutral', loading || pageSafe >= totalPages)} disabled={loading || pageSafe >= totalPages} onClick={() => setPage(totalPages)}>
              ⏭
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={smallText}>Rows</div>
            <select value={String(pageSize)} onChange={(e) => setPageSize(Number(e.target.value) || 10)} style={{ ...inputStyle, width: 90, height: 34 }}>
              {[10, 20, 50, 100].map((n) => (
                <option key={n} value={String(n)}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminResearchDeskScreen;
